// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useState, useEffect, useRef } from 'react';

import { extractErrorMessage } from '@evolvemep/api-ts';
import {
  Anchor,
  Button,
  Divider,
  Flex,
  // oxlint-disable-next-line no-restricted-imports
  Modal,
  Text,
} from '@mantine/core';
import { Notifications, notifications, useNotifications } from '@mantine/notifications';
import { useLocation } from 'react-router';

import { hiPriNotificationPrefix } from 'app/MessageCenter/types';
import { isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

type MantineNotificationsContextType = {
  showError: (err: unknown, autoClose?: number | boolean) => void;
};
const MantineNotificationsContext = React.createContext<MantineNotificationsContextType | undefined>(undefined);

const useCleanupNotificationsOnNavigation = () => {
  const location = useLocation();
  const { notifications: notificationState } = useNotifications();
  const cleanStickyNotifications = useCallback(() => {
    notificationState.forEach((notification) => {
      if (notification.autoClose === false && notification.id && !notification.id.startsWith(hiPriNotificationPrefix)) {
        notifications.hide(notification.id);
      }
    });
  }, [notificationState]);
  const cleanStickyNotificationsRef = useRef(cleanStickyNotifications);
  useEffect(() => {
    cleanStickyNotificationsRef.current = cleanStickyNotifications;
  }, [cleanStickyNotifications]);
  useEffect(() => {
    cleanStickyNotificationsRef.current();
    // when location changes, clean up any sticky notifications
  }, [location.pathname]);
};

export const NOTIFICATIONS_ZINDEX = 999999;
const maxErrorLength = 300;
export const MantineNotificationsProvider = ({ children }: { children: ReactNode }) => {
  useCleanupNotificationsOnNavigation();
  const [fullError, setFullError] = useState<string | null>(null);
  const showError = useCallback<MantineNotificationsContextType['showError']>((err, autoClose = 10000) => {
    const extractedError = extractErrorMessage(err);
    const errorTooLong = extractedError.length > maxErrorLength;
    notifications.show({
      id: 'error',
      title: 'Something went wrong',
      autoClose,
      message: (
        <Flex direction="column">
          {errorTooLong ? `${extractedError.substring(0, maxErrorLength)}...` : extractedError}
          {errorTooLong && (
            <Flex my={3} justify="flex-end">
              <Anchor
                onClick={() => {
                  notifications.hide('error');
                  setFullError(extractedError);
                }}
              >
                View more
              </Anchor>
            </Flex>
          )}
        </Flex>
      ),
      color: 'red',
    });
  }, []);
  return (
    <MantineNotificationsContext value={useMemo(() => ({ showError }), [showError])}>
      <Notifications zIndex={NOTIFICATIONS_ZINDEX} autoClose={4000} />
      {}
      <Modal
        title="Error"
        centered
        size="xl"
        opened={isNotNil(fullError)}
        onClose={() => setFullError(null)}
        styles={{ body: { maxHeight: 500 } }}
        /* 2 higher than Pendo */
        zIndex={300002}
      >
        <Text ff="monospace">{fullError}</Text>
        <Divider my="md" />
        <Flex justify="flex-end">
          <Button mb="md" color="gray" onClick={() => setFullError(null)}>
            Close
          </Button>
        </Flex>
      </Modal>
      {children}
    </MantineNotificationsContext>
  );
};

export const useMantineNotifications = () => useGeneralContext(MantineNotificationsContext, 'MantineNotifications');
